/**
 * Renderer registry helpers.
 *
 * The registry is a plain object keyed by `StepType`. We expose a small
 * `defineRegistry` helper that:
 *   1. Asserts each entry's `type` matches its registry key (catches typos
 *      where a renderer is filed under the wrong `StepType`).
 *   2. Returns a frozen registry so consumers can't mutate it at runtime.
 *
 * Per PLAN.md §6.2, concrete renderers live in `src/renderers/<type>.tsx`
 * and ship in NX-9301..NX-9304 — this file ONLY provides the plumbing.
 */

import type { ZodType } from "zod";
import type { ReactElement } from "react";

import type { StepType } from "@nextday/funnel-engine";
import type { RendererProps, StepRenderer, StepRendererRegistry } from "@survey-kit/types";

/**
 * Construct a renderer for a specific `StepType` while preserving the
 * `data` payload's typed shape inside the Component but exposing the
 * registry-friendly `StepRenderer<unknown>` outside.
 *
 * Why the cast: `StepRenderer<TData>` is contravariant in `TData` (Component
 * accepts it as input), so `StepRenderer<InfoScreenData>` does not widen to
 * `StepRenderer<unknown>` automatically. SurveyStep validates `data` against
 * `dataSchema` BEFORE calling `Component`, so the cast is sound at runtime —
 * Component always sees a `TData`-shaped payload by the time it runs.
 */
type RendererSpec<TData> = {
  type: StepType;
  dataSchema: ZodType<TData>;
  label?: string;
  wideLayout?: boolean;
  invisible?: boolean;
  Component: (props: RendererProps<TData>) => ReactElement | null;
};

export const createRenderer = <TData>(spec: RendererSpec<TData>): StepRenderer<unknown> => {
  const widened: unknown = spec;
  return widened as StepRenderer<unknown>;
};

/** True if the registry has a renderer for the given `StepType`. */
export const hasRenderer = (registry: StepRendererRegistry, type: StepType): boolean =>
  Object.prototype.hasOwnProperty.call(registry, type);

/** True if the renderer for the given `StepType` declares `wideLayout`. */
export const isWideLayout = (registry: StepRendererRegistry, type: StepType): boolean =>
  registry[type]?.wideLayout === true;

/** True if the renderer for the given `StepType` declares `invisible`. */
export const isInvisible = (registry: StepRendererRegistry, type: StepType): boolean =>
  registry[type]?.invisible === true;

/**
 * Look up a renderer or return `null` when missing. Callers decide whether
 * to throw, render a placeholder, or fall through.
 */
export const lookupRenderer = (
  registry: StepRendererRegistry,
  type: StepType,
): StepRenderer<unknown> | null => registry[type] ?? null;

/**
 * Build a frozen registry. Throws synchronously if any entry's `type`
 * disagrees with its registry key, so a renderer mis-filed under the wrong
 * key fails at module load instead of confusing the runtime.
 */
export const defineRegistry = (
  entries: ReadonlyArray<StepRenderer<unknown>>,
): StepRendererRegistry => {
  const registry: StepRendererRegistry = {};
  for (const entry of entries) {
    if (registry[entry.type]) {
      throw new Error(`@nextday/survey-kit: duplicate renderer for StepType "${entry.type}"`);
    }
    registry[entry.type] = entry;
  }
  return Object.freeze(registry);
};
